import { AnalyticsEvent, Tracker } from './tracker';
import type { AssetType as AnalyticsAssetType } from './events';
import { assetMainTypeToAnalyticsType } from './asset-type-map';
import { AssetMainType } from '../models/asset';

export function trackAssetCreateStarted(
  assetType: AnalyticsAssetType,
  source: string,
): void {
  Tracker.trackAssetCreate('started', assetType, { source });
}

export function trackAssetCreateCompleted(assetType: AnalyticsAssetType): void {
  Tracker.trackAssetCreate('completed', assetType);
}

export function trackAssetCreateFailed(
  assetType: AnalyticsAssetType,
  reasonCode = 'unknown',
): void {
  Tracker.trackAssetCreate('failed', assetType, { reason_code: reasonCode });
}

export function trackAssetEditStarted(assetType: AnalyticsAssetType): void {
  Tracker.track(AnalyticsEvent.AssetEditStarted, { asset_type: assetType });
}

export function trackAssetEditCompleted(assetType: AnalyticsAssetType): void {
  Tracker.track(AnalyticsEvent.AssetEditCompleted, { asset_type: assetType });
}

export function trackAssetDetailsViewed(assetType: AnalyticsAssetType): void {
  Tracker.track(AnalyticsEvent.AssetDetailsViewed, { asset_type: assetType });
}

export function trackAssetDeleteStarted(assetType: AnalyticsAssetType): void {
  Tracker.track(AnalyticsEvent.AssetDeleteStarted, { asset_type: assetType });
}

export function trackAssetDeleteConfirmed(assetType: AnalyticsAssetType): void {
  Tracker.track(AnalyticsEvent.AssetDeleteConfirmed, { asset_type: assetType });
}

export function trackAssetDeleteCompleted(assetType: AnalyticsAssetType): void {
  Tracker.track(AnalyticsEvent.AssetDeleteCompleted, { asset_type: assetType });
}

export function trackAssetFormOutcome(
  assetType: AnalyticsAssetType,
  isEdit: boolean,
  outcome: 'success' | 'failed',
  reasonCode = 'unknown',
): void {
  if (isEdit) {
    if (outcome === 'success') trackAssetEditCompleted(assetType);
    return;
  }
  if (outcome === 'success') trackAssetCreateCompleted(assetType);
  else trackAssetCreateFailed(assetType, reasonCode);
}

export function trackAssetFormOutcomeByMainType(
  mainType: AssetMainType,
  isEdit: boolean,
  outcome: 'success' | 'failed',
  reasonCode = 'unknown',
): void {
  trackAssetFormOutcome(
    assetMainTypeToAnalyticsType(mainType),
    isEdit,
    outcome,
    reasonCode,
  );
}

export function trackChargeStarted(): void {
  Tracker.track(AnalyticsEvent.TransactionChargeStarted);
}

export function trackChargeSubmitted(assetType: AnalyticsAssetType): void {
  Tracker.track(AnalyticsEvent.TransactionChargeSubmitted, { asset_type: assetType });
}

export function trackChargeCompleted(assetType: AnalyticsAssetType): void {
  Tracker.track(AnalyticsEvent.TransactionChargeCompleted, { asset_type: assetType });
}

export function trackChargeFailed(
  assetType: AnalyticsAssetType,
  reasonCode = 'unknown',
): void {
  Tracker.track(AnalyticsEvent.TransactionChargeFailed, {
    asset_type: assetType,
    reason_code: reasonCode,
  });
}

export function trackWithdrawStarted(): void {
  Tracker.track(AnalyticsEvent.TransactionWithdrawStarted);
}

export function trackWithdrawSubmitted(
  assetType: AnalyticsAssetType,
  isTotal: boolean,
): void {
  Tracker.track(AnalyticsEvent.TransactionWithdrawSubmitted, {
    asset_type: assetType,
    is_total: isTotal,
  });
}

export function trackWithdrawCompleted(assetType: AnalyticsAssetType): void {
  Tracker.track(AnalyticsEvent.TransactionWithdrawCompleted, { asset_type: assetType });
}

export function trackWithdrawFailed(
  assetType: AnalyticsAssetType,
  reasonCode = 'unknown',
): void {
  Tracker.track(AnalyticsEvent.TransactionWithdrawFailed, {
    asset_type: assetType,
    reason_code: reasonCode,
  });
}

export function trackCustomEventSubmitted(assetType: AnalyticsAssetType): void {
  Tracker.track(AnalyticsEvent.TransactionCustomEventSubmitted, {
    asset_type: assetType,
  });
}

export function trackCustomEventCompleted(assetType: AnalyticsAssetType): void {
  Tracker.track(AnalyticsEvent.TransactionCustomEventCompleted, {
    asset_type: assetType,
  });
}

export function assetSummaryTypeToAnalytics(
  mainType?: AssetMainType,
): AnalyticsAssetType | undefined {
  if (mainType == null) return undefined;
  return assetMainTypeToAnalyticsType(mainType);
}
